import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';
import * as React from 'react';

import {cn} from '@app/lib/utils';
import {Tooltip, TooltipContent, TooltipTrigger} from '@app/components/ui/tooltip';
import {useOptionalAuth} from '@modules/auth/hooks/context/AuthContext';
import type {PermissionRequirement} from '@modules/auth/lib/permission.shared';
import {useTranslation} from 'react-i18next';

const buttonVariants = cva(
    "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 data-[permission-denied=true]:cursor-not-allowed data-[permission-denied=true]:bg-muted data-[permission-denied=true]:text-muted-foreground data-[permission-denied=true]:shadow-none data-[permission-denied=true]:opacity-60 data-[permission-denied=true]:hover:bg-muted data-[permission-denied=true]:hover:text-muted-foreground [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
                destructive:
                    'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40',
                outline: 'border border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground',
                secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
                ghost: 'hover:bg-accent hover:text-accent-foreground',
                link: 'text-primary underline-offset-4 hover:underline',
                root: '',
            },
            size: {
                default: 'h-9 px-4 py-2 has-[>svg]:px-3',
                sm: 'h-8 rounded-md px-3 has-[>svg]:px-2.5',
                lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
                icon: 'size-9',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    },
);

function Button({
    className,
    variant,
    size,
    asChild = false,
    type = 'button',
    permissions,
    ...props
}: React.ComponentProps<'button'> & VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
    permissions?: PermissionRequirement;
}) {
    // Optional so shared UI (this Button) can render in the tablet PWA, which
    // never mounts an AuthProvider. When absent → treat as no permission check
    // (tablet does device-JWT auth via MQTT, not per-user permissions).
    const auth = useOptionalAuth();
    const hasPermission = auth?.hasPermission ?? (() => true);
    const {t} = useTranslation();
    const permissionDenied = permissions !== undefined && !hasPermission(permissions);

    // Allowed (or no permissions prop): transparent pass-through, single DOM
    // element — safe for outer <AlertDialogTrigger asChild>, <TooltipTrigger
    // asChild>, etc. to compose over.
    if (!permissionDenied) {
        const Comp = asChild ? Slot : 'button';
        return (
            <Comp
                data-slot="button"
                className={cn(buttonVariants({variant, size, className}), 'cursor-pointer')}
                type={type}
                {...props}
            />
        );
    }

    // Denied: build a single denied DOM element, then wrap in a Radix Tooltip
    // whose Root/Provider render no DOM at this position. Trigger asChild
    // dissolves into the element; Content is portaled to document.body. From
    // the outer tree's perspective, Button still emits one node at its
    // position — outer asChild wrappers merge into the Tooltip Root (props
    // dropped), which is semantically correct for a denied button (it should
    // not act as a dialog trigger, form submitter, etc).
    const blockClick = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
    };
    const denyProps = {
        'aria-disabled': true,
        'data-permission-denied': 'true',
        'data-slot': 'button',
        onClick: blockClick,
        onAuxClick: blockClick,
    } as const;

    let element: React.ReactElement;
    if (asChild) {
        const child = React.Children.only(props.children) as React.ReactElement<Record<string, unknown>>;
        const childProps = child.props as {href?: unknown; to?: unknown; className?: string; children?: React.ReactNode};
        const isNavigable = childProps.href !== undefined || childProps.to !== undefined || child.type === 'a';
        // Downgrade any navigable child (AppLink, <a>, react-router Link) to a
        // <span role="link"> so middle-click / ctrl-click / right-click
        // "Open in new tab" have no href to navigate to.
        if (isNavigable) {
            element = (
                <span
                    role="link"
                    tabIndex={0}
                    {...denyProps}
                    className={cn(
                        buttonVariants({variant, size, className}),
                        'cursor-not-allowed',
                        childProps.className,
                    )}
                >
                    {childProps.children}
                </span>
            );
        } else {
            element = React.cloneElement(child, {
                ...denyProps,
                tabIndex: 0,
                className: cn(
                    buttonVariants({variant, size, className}),
                    'cursor-not-allowed',
                    childProps.className,
                ),
            });
        }
    } else {
        // aria-disabled (not native disabled) so the element stays keyboard-
        // focusable — Radix Tooltip opens on focus, screen readers announce
        // the denial message.
        element = (
            <button
                {...props}
                type={type}
                {...denyProps}
                className={cn(buttonVariants({variant, size, className}), 'cursor-not-allowed')}
            />
        );
    }

    return (
        <Tooltip>
            <TooltipTrigger asChild>{element}</TooltipTrigger>
            <TooltipContent>
                {t('components.permissions.deniedAction', {
                    defaultValue: "You don't have permission to perform this action.",
                })}
            </TooltipContent>
        </Tooltip>
    );
}

export {Button, buttonVariants};
